/**
 * This file was copied from dashboard into clowncar as part of a cleanup effort
 */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { core } from '@tock/coretypes';
import { dashboard } from 'types';
import { noop } from 'es-toolkit';
import { pick } from 'es-toolkit/compat';
import AppConstants from 'constants/AppConstants';
import protos from 'utils/protos';

const {
  AccountCreation,
  AuthRequest,
  DeviceState,
  ResetTokenRequest,
  MfaVerifyRequest,
  MfaResendRequest,
  Patron,
} = protos;

// Returns a promise in order to chain logic after the active business is set.
export function setActive(businessId: number): Promise<unknown> {
  return Promise.resolve({
    type: AppConstants.AUTHENTICATION_SET_ACTIVE_DONE,
    payload: businessId,
  });
}

export function logout(): BaseTockAction {
  return {
    type: AppConstants.AUTHENTICATION_CLEARED_DONE,
  };
}

export function updateAuthenticationRoles(currentBusinessId?: number): ApiAction {
  return {
    TockApi: {
      getProto:
        '/authenticate/roles' +
        (currentBusinessId ? `?currentBusinessId=${currentBusinessId}` : ''),
      type: AppConstants.AUTHENTICATION_ROLES_UPDATE,
    },
  };
}

export function authenticate(email: string, password: string): ApiAction {
  const request = AuthRequest.fromObject({
    email,
    password,
    type: 'ADMIN_UI',
  });

  return {
    TockApi: {
      postProto: '/authenticate',
      request,
      type: AppConstants.AUTHENTICATION_RESPONSE,
    },
  };
}

export function dashbaordInitiateAuth(
  email: string,
  password: string
): ApiAction<core.DashboardAuthResponse> {
  const request = AuthRequest.fromObject({
    email,
    password,
    type: 'ADMIN_UI',
  });

  return {
    TockApi: {
      postProto: '/dashboard/authenticate/initiate',
      request,
      type: AppConstants.DASHBOARD_INITIATE_AUTH,
      onError: {
        5001: noop,
        5034: noop,
        defaultToOriginal: true,
      },
    },
  };
}

export function dashboardMfaVerify(
  mfaFactor: core.IMfaFactor,
  mfaCode: string,
  otpSessionUuid: string
): ApiAction<core.AuthResponse> {
  const request = MfaVerifyRequest.fromObject({
    mfaFactor,
    mfaCode,
    otpSessionUuid,
  });

  return {
    TockApi: {
      postProto: '/dashboard/authenticate/mfa/verify',
      request,
      type: AppConstants.DASHBOARD_MFA_VERIFY,
      onError: {
        5036: noop,
        defaultToOriginal: true,
      },
    },
  };
}

export function dashboardMfaResendCode(
  mfaFactor: core.IMfaFactor,
  otpSessionUuid: string
): ApiAction<core.AuthResponse> {
  const request = MfaResendRequest.fromObject({
    mfaFactor,
    otpSessionUuid,
  });

  return {
    TockApi: {
      postProto: '/dashboard/authenticate/mfa/resend',
      request,
      type: AppConstants.DASHBOARD_MFA_RESEND_CODE,
    },
  };
}

export function getLoginProviderType(email: string) {
  return {
    TockApi: {
      getProto: `/authenticate/sso/domain?email=${email}`,
      type: AppConstants.NULL,
    },
  };
}

export function getAuthenticationMethods(email: string): ApiAction {
  return {
    TockApi: {
      getListProto: `/authenticate/methods?email=${encodeURIComponent(email)}`,
      type: AppConstants.AUTHENTICATION_METHODS,
    },
  };
}

export function resetPassword(email: string): ApiAction {
  return {
    TockApi: {
      getProto: `/password/reset?type=admin&email=${encodeURIComponent(email)}`,
      type: AppConstants.NULL,
      toast: `We've sent password reset instructions to ${email}. Check your inbox.`,
    },
  };
}

export function resetPasswordWithToken(requestJSON: any): ApiAction {
  const request = ResetTokenRequest.fromObject(requestJSON);
  return {
    TockApi: {
      postProto: '/password/reset/token',
      request,
      type: AppConstants.NULL,
      toast: 'Your password has been reset. You can now log in with your new password.',
    },
  };
}

export function refreshAuthFromStorage(): BaseTockAction {
  return {
    type: AppConstants.AUTH_REFRESH_FROM_STORAGE,
  };
}

export function authenticateWithInviteToken(token: string): ApiAction {
  return {
    TockApi: {
      getProto: `/patron/role/dashboard/auth?token=${encodeURIComponent(token)}`,
      type: AppConstants.AUTHENTICATION_RESPONSE,
    },
  };
}

export function createAccount(email: string, password: string): ApiAction {
  const request = AccountCreation.fromObject({
    email,
    password,
    authProvider: 'PASSWORD',
    context: 'ADMIN_UI',
  });
  return {
    TockApi: {
      postProto: '/patron',
      request,
      type: AppConstants.CREATE_ACCOUNT,
    },
  };
}

export function createAccountWithToken(
  email: string,
  password: string,
  token: string,
  marketingOptInStatus: core.MarketingOptInStatus | undefined,
  patronDetails?: any
): ApiAction {
  const request = AccountCreation.fromObject({
    email,
    password,
    token,
    tockB2cMarketingOptInStatus: marketingOptInStatus,
    authProvider: 'PASSWORD',
    context: 'ADMIN_UI',
    ...pick(patronDetails || {}, ['firstName', 'lastName', 'phone', 'phoneCountryCode', 'zipCode']),
  });
  return {
    TockApi: {
      postProto: '/patron/signup-token',
      request,
      type: AppConstants.CREATE_ACCOUNT,
    },
  };
}

export function fetchLoggedInProfile(): ApiAction {
  return {
    TockApi: {
      getProto: '/patron',
      type: AppConstants.PATRON_FETCH_PROFILE,
    },
  };
}

export function updateProfile(profile: core.IPatron): ApiAction {
  return {
    TockApi: {
      putProto: '/patron',
      request: Patron.fromObject(profile),
      type: AppConstants.PATRON_SET_PROFILE,
    },
  };
}

export function setOverrideLockout(): Promise<unknown> {
  return Promise.resolve({
    type: AppConstants.OVERRIDE_LOCKOUT,
    payload: {},
  });
}

export function sendTelemetry(deviceState: dashboard.IDeviceState): ApiAction {
  return {
    TockApi: {
      postProto: '/telemetry/device/state',
      request: DeviceState.fromObject(deviceState),
      type: AppConstants.NULL,
    },
  };
}

export function setApolloClientConnectionState(connectionState: boolean): BaseTockAction {
  return {
    type: AppConstants.APOLLO_CLIENT_CONNECTION_STATE,
    payload: connectionState,
  };
}
